import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// SCADA Override (level 80) — the Control rung.
//
// Note what is NOT in the props: `busy`. The host sets it for every action including a poll, and this view
// polls two or three times a second, so anything gated on it would flicker and drop the clicks that land
// during a poll. Load Balancer's node buttons were unusable for exactly that.
//
// WHAT THIS CONSOLE SHOWS, AND THE ONE THING IT REFUSES TO. It shows the coupling matrix, the bands, the
// time constants, the stroke rates, and what every sensor is REPORTING right now. It does not show where
// any variable is heading for the current valve positions — no forecast, anywhere. Publishing that would
// be publishing the answer: a player could nudge sliders until the forecast looked right and never do the
// arithmetic the rung is made of. That was measured rather than assumed, with a hill-climber that nudges,
// polls, keeps what helps and undoes what hurts: it takes 0% of boards.
//
// A spoofed sensor reports its frozen figure to the player exactly as it does to the safety system. The
// price of blinding it is being blind yourself.

interface NodeV {
  name: string;
  unit: string;
  band: number;
  tau: number;
  reported: number;
  spoofed: boolean;
  spoofEndsMs: number;
  outForSec: number;
  isTarget: boolean;
}

interface ValveV {
  name: string;
  rate: number;
  commanded: number;
  actual: number;
  speed: number;
}

interface Challenge {
  started: boolean;
  nowMs: number;
  startedAtMs: number;
  playMs: number;
  timeLimitSec: number;
  readingAllowanceSec: number;
  spoofsLeft: number;
  spoofsGranted: number;
  spoofSec: number;
  graceSec: number;
  holdSec: number;
  nominal: number;
  valveHome: number;
  target: number;
  threshold: number;
  heldSec: number;
  commands: number;
  gain: number[][];
  nodes: NodeV[];
  valves: ValveV[];
}

const POLL_MS = 400;

export default function ScadaOverrideView({
  challenge, onAct, onSubmit, setPendingSubmission, startedAgoMs,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [done, setDone] = useState(false);
  const [want, setWant] = useState<number[]>([]);
  const [ramp, setRamp] = useState<string[]>([]);
  const [note, setNote] = useState<string | null>(null);

  const sendingRef = useRef(false);
  const startedRef = useRef(challenge?.started ?? false);
  const doneRef = useRef(false);

  // The session's clock is the server's; anchoring to mount would leave a resumed attempt behind.
  const anchorRef = useRef<{ key: unknown; at: number }>({ key: challenge, at: Date.now() - startedAgoMs });
  if (anchorRef.current.key !== challenge) anchorRef.current = { key: challenge, at: Date.now() - startedAgoMs };

  const nodes = useMemo(() => live?.nodes ?? [], [live]);
  const valves = useMemo(() => live?.valves ?? [], [live]);
  const started = live?.started ?? false;

  useEffect(() => {
    setLive(challenge ?? null);
    setDone(false);
    setNote(null);
    setWant(challenge?.valves?.map(v => v.commanded) ?? []);
    setRamp(challenge?.valves?.map(() => '') ?? []);
    startedRef.current = challenge?.started ?? false;
    doneRef.current = false;
    sendingRef.current = false;
  }, [challenge]);

  useEffect(() => { setPendingSubmission({}); }, [setPendingSubmission]);

  const send = useCallback(async (action: unknown) => {
    if (sendingRef.current || doneRef.current) return;
    sendingRef.current = true;
    try {
      const next = await onAct(action);
      if (next) {
        setLive(next);
        startedRef.current = next.started;
        if (next.valves?.length && want.length !== next.valves.length) {
          setWant(next.valves.map(v => v.commanded));
          setRamp(next.valves.map(() => ''));
        }
      } else {
        doneRef.current = true;
        setDone(true);
      }
    } finally {
      sendingRef.current = false;
    }
  }, [onAct, want.length]);

  // The plant moves on the server's clock whether or not the player does anything, so it has to be asked.
  // The brief costs no requests at all: the gate is a ref, not a dependency, so the interval is not rebuilt
  // when the attempt starts.
  useEffect(() => {
    const id = setInterval(() => {
      if (!startedRef.current || doneRef.current || sendingRef.current) return;
      void send({ kind: 'poll' });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [send]);

  const mono: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };
  const small: React.CSSProperties = { ...mono, fontSize: '0.72rem' };

  if (!started) {
    return (
      <div style={{ fontFamily: 'monospace' }}>
        <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117' }}>
          <p style={{ ...mono, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Five process variables and four valves. Every valve moves <strong>several variables at
            once</strong>, by a coupling matrix you will be able to read, and every variable drifts toward
            the value its valves imply with its own time constant — nothing jumps.
          </p>
          <p style={{ ...mono, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Each variable has a <strong>safety band</strong>. If the value the safety system SEES leaves
            that band for more than {live?.graceSec ?? 1}s, the plant trips and you are done. Your job is
            to push <strong>one</strong> named variable past its sabotage threshold and hold it there for{' '}
            {live?.holdSec ?? 8}s.
          </p>
          <p style={{ ...mono, color: '#60a5fa', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Which follows through in three steps. Taking the target past its threshold takes it out of its
            own band by definition, so its sensor has to be <strong>spoofed</strong> — frozen at its last
            reading for {live?.spoofSec ?? 20}s. But the couplings drag the neighbours along, so opening the
            valve that most directly drives the target puts something else out of band as well. You can
            spend another spoof on that neighbour, or you can <strong>cancel</strong> it: combine the valves
            so their effects on the neighbour subtract while their effects on the target add. You have{' '}
            <strong>{live?.spoofsGranted ?? 1}</strong>, so cancelling is usually the better trade — and
            while a sensor is spoofed <em>you</em> cannot see the true value either.
          </p>
          <div style={{
            border: '1px solid #b45309', borderRadius: 4, padding: '0.5rem 0.7rem',
            margin: '0 0 0.6rem', background: '#1a1206',
          }}>
            <p style={{ ...mono, color: '#fbbf24', margin: 0, lineHeight: 1.6 }}>
              And the part that makes this a console rather than a worksheet. Each valve has its own stroke
              rate, so commanding a balanced combination all at once <strong>does not balance
              anything</strong> — the fast valve arrives first and the cancellation is broken while the
              slow ones are still travelling. Every command takes a <strong>ramp time</strong>: give all of
              them the <em>same</em> ramp, at least as long as the slowest valve needs, and their
              displacements stay in proportion the whole way.
            </p>
          </div>
          <p style={{ ...mono, color: 'var(--text-muted)', margin: '0 0 0.8rem' }}>
            The schematic arrives when you take the console. The clock is already running — about{' '}
            {live?.readingAllowanceSec ?? 35}s of it is set aside for this.
          </p>
          <button
            className="primary"
            onClick={() => void send({ kind: 'begin' })}
            style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
          >
            Take the console
          </button>
        </div>
      </div>
    );
  }

  const nominal = live?.nominal ?? 50;
  const held = live?.heldSec ?? 0;
  const holdNeed = live?.holdSec ?? 8;
  const target = live?.target ?? -1;

  return (
    <div style={{ fontFamily: 'monospace' }}>
      {/* Fixed widths everywhere: a figure going 9.9 → -11.4 must not shove its neighbour. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap', whiteSpace: 'nowrap',
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.55rem', background: '#0d1117',
      }}>
        <span style={{ ...mono, minWidth: '15rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>TARGET </span>
          <strong style={{ color: '#f87171' }}>{nodes[target]?.name ?? '—'}</strong>
          <span style={{ color: '#9aa4b2' }}> past {(live?.threshold ?? 0).toFixed(0)}</span>
        </span>
        <span style={{ ...mono, minWidth: '9rem', color: held > 0 ? '#4ade80' : 'var(--text-muted)' }}>
          held <strong>{held.toFixed(1)}</strong> / {holdNeed.toFixed(0)}s
        </span>
        <span style={{ ...mono, minWidth: '8.5rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>SPOOFS </span>
          <strong style={{ color: (live?.spoofsLeft ?? 0) > 0 ? '#cbd5e1' : '#f87171' }}>
            {live?.spoofsLeft ?? 0}
          </strong>
          <span style={{ color: '#4b5563' }}> / {live?.spoofsGranted ?? 0}</span>
        </span>
        <span style={{ ...small, minWidth: '13rem', color: '#4b5563' }}>
          spoof {live?.spoofSec ?? 20}s · grace {live?.graceSec ?? 1}s · {live?.commands ?? 0} commands
        </span>
      </div>

      {/* The plant. Wide, so it scrolls inside its own box rather than the page. */}
      <div style={{ overflowX: 'auto', marginBottom: '0.55rem' }}>
        <div style={{ minWidth: 640 }}>
          <div style={{
            display: 'flex', gap: '0.4rem', padding: '0.2rem 0.5rem', whiteSpace: 'nowrap',
            ...small, color: '#4b5563', borderBottom: '1px solid #1c2029',
          }}>
            <span style={{ minWidth: '9.5rem' }}>variable</span>
            <span style={{ minWidth: '3.4rem', textAlign: 'right' }}>band</span>
            <span style={{ minWidth: '3rem', textAlign: 'right' }}>tau</span>
            <span style={{ minWidth: '5.4rem', textAlign: 'right' }}>reported</span>
            <span style={{ minWidth: '5.5rem' }}>in band</span>
            {valves.map(v => (
              <span key={v.name} style={{ minWidth: '3.6rem', textAlign: 'right', color: '#6b7280' }}>
                {v.name}
              </span>
            ))}
            <span style={{ minWidth: '5.4rem' }}>sensor</span>
          </div>

          {nodes.map((n, i) => {
            const lo = nominal - n.band, hi = nominal + n.band;
            const outOfBand = n.reported < lo || n.reported > hi;
            const frac = Math.max(0, Math.min(1, (n.reported - (nominal - n.band * 2)) / (n.band * 4)));
            return (
              <div key={n.name} style={{
                display: 'flex', gap: '0.4rem', alignItems: 'center', whiteSpace: 'nowrap',
                padding: '0.2rem 0.5rem',
                background: n.isTarget ? '#1a1013' : 'transparent',
                borderLeft: `2px solid ${n.isTarget ? '#f87171' : 'transparent'}`,
              }}>
                <span style={{ ...small, minWidth: '9.5rem', color: n.isTarget ? '#fca5a5' : '#cbd5e1' }}>
                  {n.name} <span style={{ color: '#4b5563' }}>{n.unit}</span>
                </span>
                <span style={{ ...small, minWidth: '3.4rem', textAlign: 'right', color: 'var(--text-muted)' }}>
                  ±{n.band.toFixed(0)}
                </span>
                <span style={{ ...small, minWidth: '3rem', textAlign: 'right', color: 'var(--text-muted)' }}>
                  {n.tau.toFixed(1)}s
                </span>
                <span style={{
                  ...mono, minWidth: '5.4rem', textAlign: 'right',
                  color: n.spoofed ? '#a78bfa' : outOfBand ? '#f87171' : '#e2e8f0',
                }}>
                  {n.reported.toFixed(1)}
                </span>
                <span style={{ minWidth: '5.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                  <span style={{
                    width: 46, height: 7, background: '#161b22', borderRadius: 2, position: 'relative',
                    flex: '0 0 auto', overflow: 'hidden',
                  }}>
                    <span style={{
                      position: 'absolute', left: '25%', width: '50%', top: 0, bottom: 0,
                      background: '#1f2937',
                    }} />
                    <span style={{
                      position: 'absolute', left: `${frac * 100}%`, top: -1, bottom: -1, width: 2,
                      background: outOfBand ? '#f87171' : '#4ade80',
                    }} />
                  </span>
                  <span style={{ ...small, minWidth: '2.2rem', color: n.outForSec > 0 ? '#f87171' : '#4b5563' }}>
                    {n.outForSec > 0 ? `${n.outForSec.toFixed(1)}s` : ''}
                  </span>
                </span>
                {valves.map((v, j) => (
                  <span key={v.name} style={{
                    ...small, minWidth: '3.6rem', textAlign: 'right',
                    color: (live?.gain?.[i]?.[j] ?? 0) === 0 ? '#374151' : '#fbbf24',
                  }}>
                    {(live?.gain?.[i]?.[j] ?? 0) > 0 ? '+' : ''}{live?.gain?.[i]?.[j] ?? 0}
                  </span>
                ))}
                <span style={{ minWidth: '5.4rem' }}>
                  {n.spoofed ? (
                    <span style={{ ...small, color: '#a78bfa' }}>frozen</span>
                  ) : (
                    <button
                      onClick={() => {
                        if ((live?.spoofsLeft ?? 0) <= 0) { setNote('No sensor spoofs left.'); return; }
                        void send({ kind: 'spoof', index: i });
                      }}
                      style={{
                        ...small, padding: '0.05rem 0.35rem', border: '1px solid #5b21b6',
                        background: 'transparent', color: '#a78bfa', borderRadius: 3, cursor: 'pointer',
                      }}
                      title={`Freeze this reading for ${live?.spoofSec ?? 20}s — you will not see the truth either`}
                    >
                      blind
                    </button>
                  )}
                </span>
              </div>
            );
          })}

          <div style={{
            display: 'flex', gap: '0.4rem', padding: '0.2rem 0.5rem', whiteSpace: 'nowrap',
            ...small, color: '#4b5563', borderTop: '1px solid #1c2029',
          }}>
            <span style={{ minWidth: '9.5rem' }}>gains are units per 10 points</span>
            <span style={{ minWidth: '3.4rem' }} />
            <span style={{ minWidth: '3rem' }} />
            <span style={{ minWidth: '5.4rem', textAlign: 'right' }}>stroke</span>
            <span style={{ minWidth: '5.5rem' }} />
            {valves.map(v => (
              <span key={v.name} style={{ minWidth: '3.6rem', textAlign: 'right', color: '#9aa4b2' }}>
                {v.rate.toFixed(0)}/s
              </span>
            ))}
            <span style={{ minWidth: '5.4rem' }} />
          </div>
        </div>
      </div>

      {/* The commands. One row per valve, always mounted, always the same height. */}
      <div style={{ display: 'grid', gap: '0.25rem', marginBottom: '0.5rem' }}>
        {valves.map((v, j) => {
          const moving = Math.abs(v.actual - v.commanded) > 0.15;
          return (
            <div key={v.name} style={{
              display: 'flex', gap: '0.4rem', alignItems: 'center', whiteSpace: 'nowrap', flexWrap: 'wrap',
              border: '1px solid #1c2029', borderRadius: 4, padding: '0.25rem 0.5rem', background: '#0d1117',
            }}>
              <strong style={{ ...mono, minWidth: '4.6rem', color: '#e2e8f0' }}>{v.name}</strong>
              <span style={{ ...small, minWidth: '9.5rem', color: 'var(--text-muted)' }}>
                at <strong style={{ color: moving ? '#fbbf24' : '#cbd5e1' }}>{v.actual.toFixed(1)}</strong>
                {' → '}{v.commanded.toFixed(1)}
                {moving ? ` @${v.speed.toFixed(1)}/s` : ''}
              </span>
              <input
                type="number"
                value={want[j] ?? v.commanded}
                min={0}
                max={100}
                onChange={e => setWant(a => a.map((x, k) => k === j ? Number(e.target.value) : x))}
                style={{ ...small, width: '4.8rem', padding: '0.1rem 0.3rem', textAlign: 'right' }}
              />
              <span style={{ ...small, color: '#4b5563' }}>over</span>
              <input
                type="number"
                value={ramp[j] ?? ''}
                min={0}
                max={60}
                step={0.5}
                placeholder="0"
                onChange={e => setRamp(a => a.map((x, k) => k === j ? e.target.value : x))}
                style={{ ...small, width: '4.2rem', padding: '0.1rem 0.3rem', textAlign: 'right' }}
              />
              <span style={{ ...small, color: '#4b5563' }}>s</span>
              <button
                onClick={() => {
                  const to = Math.max(0, Math.min(100, want[j] ?? v.commanded));
                  const over = Math.max(0, Math.min(60, Number(ramp[j]) || 0));
                  void send({ kind: 'set', index: j, value: to, overSec: over });
                }}
                style={{ ...small, padding: '0.15rem 0.5rem', minWidth: '5.2rem' }}
              >
                command
              </button>
            </div>
          );
        })}
      </div>

      <div style={{ minHeight: '1.3rem', marginBottom: '0.2rem', overflow: 'hidden' }}>
        {note && <span style={{ ...small, color: '#fbbf24' }}>{note}</span>}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => {
            const over = valves.map((v, j) => Math.abs((want[j] ?? v.commanded) - v.actual) / v.rate);
            const common = Math.max(...over.filter(x => x > 0), 0);
            setRamp(valves.map(() => common > 0 ? common.toFixed(1) : ''));
          }}
          style={{ ...small, padding: '0.25rem 0.6rem' }}
          title="Fill every ramp with the time the slowest of your intended moves needs"
        >
          match the ramps
        </button>
        <button
          onClick={() => onSubmit({})}
          style={{ ...small, padding: '0.25rem 0.6rem', marginLeft: 'auto' }}
          title="Stop here — the plant is graded on what your commands actually did"
        >
          Walk away
        </button>
        {done && <span style={{ ...small, color: '#4b5563' }}>settled</span>}
      </div>
    </div>
  );
}
